/**
 * Render a numeric bound (min, max, maximum length) the way the reader's
 * language writes numbers.
 *
 * Bounds come out of the component JSON as plain strings — `"10000"`,
 * `"0.5"` — and used to be dropped into messages verbatim. A German visitor
 * who typed 100000 was told "Höchstens 10000", which is exactly the sort of
 * number nobody can read at a glance and the one moment where they are
 * already comparing digits. Now it says "Höchstens 10.000".
 *
 * Deliberately separate from {@link import('../ui/characteristics/formatCalculatedValue')}:
 * that one pins the decimal places an admin configured for a calculated value,
 * while a bound shows the digits it happens to have — `40` stays `40`, `0.5`
 * becomes `0,5`.
 *
 * Anything that is not a number round-trips unchanged. Bounds are
 * admin-authored and occasionally hold something else entirely; a validation
 * message is the wrong place to start swallowing content.
 */
export function formatBound(value: string | number | undefined, locale: string | undefined): string {
    if (value === undefined || value === null || value === '') return '';
    const raw = String(value).trim();
    if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return raw;

    const n = Number(raw);
    if (!Number.isFinite(n)) return raw;
    const decimals = raw.split('.')[1]?.length ?? 0;

    try {
        return new Intl.NumberFormat((locale ?? 'en_US').replace('_', '-'), {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(n);
    } catch {
        // A locale slug Intl refuses (legacy underscore forms reach us too):
        // the unformatted number is still correct, just harder to read.
        return raw;
    }
}
